import React, { useState } from 'react';

import dayjs, { Dayjs } from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { Button, DatePicker, Flex, Popconfirm, Tabs, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

interface TempPeriod {
    id: string;
    ending_at: number;
    from_plan_periodic?: boolean;
}

interface LeedbucksTempTabsProps<T extends TempPeriod> {
    periods: T[];
    onChange?: (periods: T[]) => void;
    isReadOnly?: boolean;
    createPeriod: (id: string, ending_at: number) => T; // a new period, empty until filled in its tab
    renderPeriod: (period: T, update: (changes: Partial<T>) => void) => React.ReactNode; // what the period sets
    emptyLabel: string;
}

// The end day is excluded: the period stops at midnight on that day
const toEndingAt = (day: Dayjs): number => day.startOf('day').valueOf();
const isPastEndDay = (day: Dayjs): boolean => !day.isAfter(dayjs(), 'day');

export default function LeedbucksTempTabs<T extends TempPeriod>({ periods, onChange, isReadOnly = false, createPeriod, renderPeriod, emptyLabel }: LeedbucksTempTabsProps<T>) {
    const [newEndDay, setNewEndDay] = useState<Dayjs | null>(null);
    const [activeKey, setActiveKey] = useState<string>();
    const [openedAt] = useState(Date.now); // flags the periods already over when the modal opened

    // Oldest first, the order they are read in
    const sorted = [...periods].sort((a, b) => a.ending_at - b.ending_at);
    const resolvedActiveKey = sorted.some((period) => period.id === activeKey) ? activeKey : sorted[0]?.id;

    const updatePeriod = (id: string, changes: Partial<T>) =>
        onChange?.(periods.map((period) => period.id === id ? { ...period, ...changes } : period));

    const addPeriod = () => {
        if (!newEndDay) return;
        const id = uuidv4();
        onChange?.([...periods, createPeriod(id, toEndingAt(newEndDay))]);
        setNewEndDay(null);
        setActiveKey(id);
    };

    return <Flex vertical gap={16}>
        {!isReadOnly && <Flex gap={8} align="center">
            <DatePicker
                value={newEndDay}
                onChange={setNewEndDay}
                disabledDate={isPastEndDay}
                format="DD/MM/YYYY"
                placeholder="Date de fin (exclue)"
            />
            <Button type="primary" icon={<PlusOutlined />} disabled={!newEndDay} onClick={addPeriod}>Ajouter une période</Button>
        </Flex>}

        {sorted.length === 0
            ? <Typography.Text type="secondary">{emptyLabel}</Typography.Text>
            : <Tabs
                activeKey={resolvedActiveKey}
                onChange={setActiveKey}
                items={sorted.map((period) => ({
                    key: period.id,
                    label: <Flex gap={4} align="center">
                        Fin le {dayjs(period.ending_at).format('DD/MM/YYYY')}
                        {period.ending_at <= openedAt && <Tag color="red">Expiré</Tag>}
                        {period.from_plan_periodic && <Tooltip title="Copiée du plan à la création, suit la date d'onboarding"><Tag>Plan</Tag></Tooltip>}
                    </Flex>,
                    children: <Flex vertical gap={8}>
                        <Flex gap={8} align="center" justify="space-between">
                            <DatePicker
                                value={dayjs(period.ending_at)}
                                onChange={(day) => day && updatePeriod(period.id, { ending_at: toEndingAt(day) } as Partial<T>)}
                                disabledDate={isPastEndDay}
                                disabled={isReadOnly}
                                allowClear={false}
                                format="DD/MM/YYYY"
                            />
                            {!isReadOnly && <Popconfirm
                                title="Supprimer cette période ?"
                                okText="Supprimer"
                                cancelText="Annuler"
                                okButtonProps={{ danger: true }}
                                onConfirm={() => onChange?.(periods.filter((candidate) => candidate.id !== period.id))}
                            >
                                <Button danger icon={<DeleteOutlined />}>Supprimer la période</Button>
                            </Popconfirm>}
                        </Flex>
                        {renderPeriod(period, (changes) => updatePeriod(period.id, changes))}
                    </Flex>,
                }))}
            />}
    </Flex>;
}